<template>
  <section class="customer-detail-sales">
    <article class="customer-detail-card customer-detail-card--accent-primary">
      <SalesToolbar
        mode="sales"
        :show-customer-filter="false"
        :initial-date-preset-key="''"
        @search-change="handleToolbarSearch"
        @filters-change="handleToolbarFilters"
        @apply="applyToolbarFilters"
      />

      <StatusTabs
        :tabs="statusTabs"
        :active-tab="activeStatusTab"
        @select="selectStatusTab"
      />

      <SalesTable
        :rows="paginatedRows"
        :loading="effectiveLoading"
        :empty-text="emptyText"
        :pagination="salesPagination"
        :sort-field="sortField"
        :sort-direction="sortDirection"
        @sort-change="handleSortChange"
        @page-change="handlePageChange"
        @change-sale-status="handleChangeSaleStatus"
        @edit-sale="handleEditSale"
        @delete-sale="handleDeleteSale"
      />

      <SaleModal
        :show="showSaleEditModal"
        :mode="saleModalMode"
        :initial-data="saleModalInitialData"
        :submitting="saleSubmitting"
        :advertising-enabled="isAdvertisingModuleActive"
        @close="closeSaleModal"
        @delete="handleDeleteSaleFromModal"
        @submit="submitSaleEdit"
      />

      <ConfirmActionModal
        :show="isDeleteConfirmModalOpen"
        :title="salesText.deleteModal.title"
        :subtitle="salesText.deleteModal.subtitle"
        :message="deleteConfirmMessage"
        :confirm-text="salesText.deleteModal.confirm"
        @cancel="closeDeleteConfirmModal"
        @confirm="confirmDeleteSale"
      />

      <SaleStatusModal
        :show="isStatusModalOpen"
        :sale-name="resolveSelectedSaleName()"
        :current-status-label="resolveSelectedSaleStatusLabel()"
        :statuses="statusOptions"
        :selected-status-id="resolveSelectedSaleStatusId()"
        :submitting="statusModalSubmitting"
        @close="closeStatusModal"
        @submit="confirmUpdateSaleStatus"
      />
    </article>
  </section>
</template>

<script>
import SalesToolbar from '@/components/sales/shared/SalesToolbar.vue';
import StatusTabs from '@/components/common/StatusTabs.vue';
import SalesTable from '@/components/sales/shared/SalesTable.vue';
import SaleStatusModal from '@/components/sales/shared/SaleStatusModal.vue';
import SaleModal from '@/components/sales/SaleModal.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import salesService from '@/services/sales';
import {
  createDefaultSalesToolbarFilters,
  normalizeSalesToolbarFilters,
  filterSalesRowsLocal,
  buildSalesListParams,
} from '@/composables/sales/useSalesFilters';
import { buildUpdateSaleRequestData } from '@/composables/sales/useSalesCreateModal';
import { fetchSalesRows } from '@/composables/sales/useSalesList';
import {
  buildSalesStatusTabs,
  filterRowsByStatus,
} from '@/composables/sales/useSalesStatusTabs';
import { useDomainText } from '@/composables/text/useDomainText';
import {
  createCustomerDetailSaleActionState,
  createCustomerDetailSaleActionComputed,
  createCustomerDetailSaleActionMethods,
} from '@/composables/customers/customerDetail/useCustomerDetailSaleActions';
import {
  resolveTotalPages,
  ensureValidPage,
  sliceRowsByPage,
  buildPaginationPayload,
} from '@/composables/shared/usePagination';
import { resolveSortChange, sortRows } from '@/composables/shared/useSorting';

const { catalog: text } = useDomainText('sales');
const salesText = text?.customerDetail?.sales || {
  entityLabel: 'Negocio',
  emptyText: 'Sin negocios disponibles para los filtros seleccionados.',
  deleteModal: {
    title: 'Eliminar negocio',
    subtitle: 'Esta accion eliminara el negocio seleccionado.',
    confirm: 'Eliminar',
  },
  actionMessages: {
    loadStatusOptionsError: 'No fue posible cargar los estados de negocio.',
    updateStatusError: 'No fue posible actualizar el estado del negocio.',
    updateStatusSuccess: 'El estado del negocio ha sido actualizado correctamente.',
    updateError: 'No fue posible actualizar el negocio.',
    updateSuccess: 'El negocio ha sido actualizado correctamente.',
    loadEditError: 'No fue posible cargar el negocio para edicion.',
    deleteError: 'No fue posible eliminar el negocio.',
    deleteSuccess: 'El negocio ha sido eliminado correctamente.',
  },
};

const saleActionComputed = createCustomerDetailSaleActionComputed({
  entityLabel: salesText.entityLabel,
});

const saleActionMethods = createCustomerDetailSaleActionMethods({
  readService: salesService,
  writeService: salesService,
  buildUpdateRequestData: buildUpdateSaleRequestData,
  messages: salesText.actionMessages,
});

export default {
  name: 'CustomerDetailSalesTab',
  components: {
    SalesToolbar,
    StatusTabs,
    SalesTable,
    SaleStatusModal,
    SaleModal,
    ConfirmActionModal,
  },
  emits: ['search-change', 'filters-change', 'view-sale-detail', 'change-sale-status', 'update-count'],
  props: {
    customer: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: salesText.emptyText,
    },
  },
  computed: {
    customerId() {
      return Number(this.customer?.id || 0);
    },
    salesText() {
      return salesText;
    },
    ...saleActionComputed,
    effectiveLoading() {
      return this.loading || this.tableLoading;
    },
    normalizedRows() {
      return Array.isArray(this.salesRows) ? this.salesRows : [];
    },
    statusTabs() {
      return buildSalesStatusTabs(this.normalizedRows);
    },
    filteredRows() {
      return filterRowsByStatus(this.normalizedRows, this.activeStatusTab);
    },
    sortedRows() {
      return sortRows(this.filteredRows, {
        field: this.sortField,
        direction: this.sortDirection,
      }, {
        sale_date: (row) => new Date(row?.sale_date || row?.date || 0).getTime(),
        customer: (row) => String(row?.company_name || row?.customer_name || row?.cliente || '').trim().toLowerCase(),
        sale_name: (row) => String(row?.sale_name || row?.project || row?.name || row?.code || '').trim().toLowerCase(),
        service: (row) => String(row?.service_name || row?.service || row?.service_type || '').trim().toLowerCase(),
        subtotal: (row) => Number(row?.subtotal || 0),
        balance: (row) => Number(row?.balance ?? row?.saldo ?? 0),
      });
    },
    totalPages() {
      return resolveTotalPages(this.sortedRows.length, this.pageSize);
    },
    paginatedRows() {
      return sliceRowsByPage(this.sortedRows, this.currentPage, this.pageSize);
    },
    salesPagination() {
      return buildPaginationPayload({
        currentPage: this.currentPage,
        pageSize: this.pageSize,
        totalItems: this.sortedRows.length,
      });
    },
  },
  data() {
    return {
      tableLoading: false,
      salesAllRows: [],
      salesRows: [],
      activeStatusTab: 'all',
      toolbarSearch: '',
      toolbarFilters: createDefaultSalesToolbarFilters(),
      appliedToolbarSearch: '',
      appliedToolbarFilters: createDefaultSalesToolbarFilters(),
      listRequestToken: 0,
      currentPage: 1,
      pageSize: 30,
      sortField: 'sale_date',
      sortDirection: 'desc',
      lastFetchParamsKey: '',
      hasLoadedListOnce: false,
      ...createCustomerDetailSaleActionState(),
    };
  },
  mounted() {
    this.fetchSales(true);
  },
  watch: {
    customerId(nextId, previousId) {
      if (nextId === previousId) {
        return;
      }

      this.currentPage = 1;
      this.fetchSales(true);
    },
  },
  methods: {
    buildRemoteListParams() {
      const params = buildSalesListParams({
        toolbarFilters: this.appliedToolbarFilters,
        toolbarSearch: this.appliedToolbarSearch,
      });

      const customerId = Number(this.customerId || 0);
      return {
        ...params,
        customer: customerId > 0 ? [String(customerId)] : [],
      };
    },
    buildFetchParamsKey() {
      return JSON.stringify(this.buildRemoteListParams());
    },
    ensureValidPage() {
      this.currentPage = ensureValidPage(this.currentPage, this.totalPages);
    },
    syncRowsFromAppliedState() {
      const customerId = Number(this.customerId || 0);
      const enforcedFilters = {
        ...this.appliedToolbarFilters,
        customer: customerId > 0 ? [String(customerId)] : [],
      };

      this.salesRows = filterSalesRowsLocal(
        this.salesAllRows,
        this.appliedToolbarSearch,
        enforcedFilters,
      );
      this.ensureValidPage();
    },
    async fetchSales(force = false) {
      const paramsKey = this.buildFetchParamsKey();

      if (!force && this.tableLoading && this.lastFetchParamsKey === paramsKey) {
        return;
      }

      if (!force && this.hasLoadedListOnce && this.lastFetchParamsKey === paramsKey) {
        return;
      }

      this.lastFetchParamsKey = paramsKey;
      const requestToken = ++this.listRequestToken;
      this.tableLoading = true;

      try {
        const rows = await fetchSalesRows(salesService, this.buildRemoteListParams());

        if (requestToken === this.listRequestToken) {
          this.salesAllRows = rows;
          this.syncRowsFromAppliedState();
          this.$emit('update-count', {
            key: 'sales',
            count: this.salesAllRows.length,
          });
        }
      } catch (error) {
        if (requestToken === this.listRequestToken) {
          this.salesAllRows = [];
          this.salesRows = [];
          this.$emit('update-count', {
            key: 'sales',
            count: 0,
          });
        }
      } finally {
        if (requestToken === this.listRequestToken) {
          this.tableLoading = false;
          this.hasLoadedListOnce = true;
        }
      }
    },
    handleToolbarSearch(value) {
      const nextSearch = String(value || '');
      this.toolbarSearch = nextSearch;
      this.appliedToolbarSearch = nextSearch;
      this.currentPage = 1;
      this.syncRowsFromAppliedState();
      this.$emit('search-change', this.toolbarSearch);
    },
    handleToolbarFilters(filters, meta = {}) {
      const previousDateStart = String(this.toolbarFilters?.date_start || '').trim();
      const previousDateEnd = String(this.toolbarFilters?.date_end || '').trim();
      const normalized = normalizeSalesToolbarFilters(filters);
      this.toolbarFilters = normalized;

      if (Object.prototype.hasOwnProperty.call(normalized, 'search')) {
        this.toolbarSearch = String(normalized.search || '');
      }

      const nextDateStart = String(normalized.date_start || '').trim();
      const nextDateEnd = String(normalized.date_end || '').trim();
      const dateChanged = nextDateStart !== previousDateStart || nextDateEnd !== previousDateEnd;
      const trigger = String(meta?.trigger || '').trim();

      if (dateChanged && trigger === 'date') {
        this.appliedToolbarFilters = normalized;
        this.appliedToolbarSearch = String(this.toolbarSearch || '');
        this.currentPage = 1;
        this.fetchSales(true);
      }

      this.$emit('filters-change', {
        ...normalized,
        search: this.toolbarSearch,
      });
    },
    applyToolbarFilters(payload = {}) {
      const search = Object.prototype.hasOwnProperty.call(payload, 'search')
        ? String(payload.search || '')
        : String(this.toolbarSearch || '');

      const filters = Object.prototype.hasOwnProperty.call(payload, 'filters')
        ? normalizeSalesToolbarFilters(payload.filters)
        : normalizeSalesToolbarFilters(this.toolbarFilters);

      this.toolbarSearch = search;
      this.toolbarFilters = filters;
      this.appliedToolbarSearch = search;
      this.appliedToolbarFilters = filters;
      this.currentPage = 1;
      this.fetchSales(true);
    },
    handlePageChange(nextPage) {
      const page = Number(nextPage || 1);
      if (!Number.isFinite(page)) {
        return;
      }

      this.currentPage = page;
      this.ensureValidPage();
    },
    handleSortChange(sortPayload) {
      const nextSort = resolveSortChange(
        {
          field: this.sortField,
          direction: this.sortDirection,
        },
        sortPayload,
        ['sale_date', 'customer', 'sale_name', 'service', 'subtotal', 'balance'],
      );

      if (nextSort.field === this.sortField && nextSort.direction === this.sortDirection) {
        return;
      }

      this.sortField = nextSort.field;
      this.sortDirection = nextSort.direction;
      this.currentPage = 1;
      this.ensureValidPage();
    },
    ...saleActionMethods,
    selectStatusTab(statusKey) {
      this.activeStatusTab = statusKey;
      this.currentPage = 1;
      this.ensureValidPage();
      this.$emit('filters-change', {
        ...this.appliedToolbarFilters,
        search: this.appliedToolbarSearch,
        status_tab: this.activeStatusTab,
      });
    },
  },
};
</script>

<style scoped>
.customer-detail-sales {
  display: grid;
  gap: 12px;
}
</style>
